import { useState } from "react";
import { MessageCircle, Mail, Phone, Check } from "lucide-react";
import { Container } from "../components/ui";
import { PageHeading } from "../ds/core";
import { useLocale } from "../i18n/useLocale";
import { api } from "@shared/api";

/** The published number, digits only — `wa.me` and `tel:` both build from it. */
const WHATSAPP = "32473735794";

/** At least one character that is not a space — `required` alone accepts "   ". */
const NOT_BLANK = "^(?=.*\\S).+$";
/** A domain with a dot: the browser's own `type="email"` accepts `a@b`. */
const EMAIL = "[^@\\s]+@[^@\\s]+\\.[^@\\s]{2,}";

const FIELD = "w-full rounded-xl border border-line bg-white px-4 py-3 text-[15px] text-ink outline-none transition-colors placeholder:text-ink-faint focus:border-primary";

export default function Contact() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState(false);
  const { t, locale } = useLocale();

  /**
   * Until now this form resolved to `setSent(true)` and nothing else — the
   * visitor was thanked and the message was dropped. It now posts to the API,
   * and the thank-you only appears once the API has actually accepted it.
   */
  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSending(true);
    setFailed(false);
    try {
      await api.post("/v4/leads/", {
        name: String(form.get("name") ?? "").trim(),
        company_name: String(form.get("company") ?? "").trim(),
        email: String(form.get("email") ?? "").trim(),
        message: String(form.get("message") ?? "").trim(),
        language: locale,
        page_path: typeof window === "undefined" ? "" : window.location.pathname,
      });
      setSent(true);
    } catch {
      // Keep what they typed on screen: re-typing a paragraph because of our
      // outage is not something to ask of someone trying to reach us.
      setFailed(true);
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <PageHeading
        tone="light"
        eyebrow={t("contact:hero.eyebrow")}
        title={t("contact:hero.title")}
        intro={t("contact:hero.intro")}
      />

      <section className="bg-bg">
        <Container className="grid gap-12 py-16 md:grid-cols-[1fr_1.1fr]">
          <div className="flex flex-col gap-4">
            {/* Each channel is the thing it names: a contact page that prints a number
                and makes you copy it is a contact page that does not work on a phone
                (QA 278779). The WhatsApp card opens wa.me in a new tab; e-mail and
                phone hand over to the visitor's own client. */}
            {[
              { icon: MessageCircle, t: t("contact:channel.whatsapp.title"), d: "+32 473 73 57 94", note: t("contact:channel.whatsapp.note"), href: `https://wa.me/${WHATSAPP}`, external: true },
              { icon: Mail, t: t("contact:channel.email.title"), d: "hello@done-it.app", note: t("contact:channel.email.note"), href: "mailto:hello@done-it.app" },
              { icon: Phone, t: t("contact:channel.phone.title"), d: "+32 473 73 57 94", note: t("contact:channel.phone.note"), href: `tel:+${WHATSAPP}` },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <a
                  key={c.t}
                  href={c.href}
                  {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  className="flex items-start gap-4 rounded-2xl border border-line bg-bg-subtle p-4 transition-colors hover:border-primary hover:bg-white md:p-6">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white text-primary shadow-sm">
                    <Icon size={20} strokeWidth={2} />
                  </span>
                  <div>
                    <h4 className="font-display text-[16px] font-semibold text-ink">{c.t}</h4>
                    <div className="mt-0.5 text-[15px] font-medium text-ink">{c.d}</div>
                    <div className="mt-0.5 text-[13px] text-ink-faint">{c.note}</div>
                  </div>
                </a>
              );
            })}
          </div>

          <div className="rounded-3xl border border-line bg-white p-4 md:p-8">
            {sent ? (
              <div className="flex h-full flex-col items-center justify-center gap-3 py-10 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-success-soft text-success">
                  <Check size={24} strokeWidth={2.4} />
                </span>
                <h3 className="font-display text-xl font-semibold text-ink">{t("contact:sent.title")}</h3>
                <p className="max-w-xs text-[14px] leading-relaxed text-ink-soft">{t("contact:sent.body")}</p>
              </div>
            ) : (
              <form className="flex flex-col gap-4" onSubmit={submit}>
                <h3 className="font-display text-xl font-semibold text-ink">{t("contact:form.title")}</h3>
                {/* Every field is labelled for a screen reader (the placeholder disappears
                    as soon as anyone types), bounded, and refuses what the API cannot
                    use: a name of spaces, `a@b` without a domain, an empty message —
                    all three were accepted before (QA 278779). `NOT_BLANK` is what
                    stops a field of spaces; `required` alone does not. */}
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="contents">
                    <span className="sr-only">{t("contact:form.name")}</span>
                    <input name="name" className={FIELD} placeholder={t("contact:form.name")} required pattern={NOT_BLANK} maxLength={120} autoComplete="name" />
                  </label>
                  <label className="contents">
                    <span className="sr-only">{t("contact:form.company")}</span>
                    <input name="company" className={FIELD} placeholder={t("contact:form.company")} maxLength={120} autoComplete="organization" />
                  </label>
                </div>
                <label className="contents">
                  <span className="sr-only">{t("contact:form.email")}</span>
                  <input name="email" className={FIELD} type="email" placeholder={t("contact:form.email")} required pattern={EMAIL} maxLength={254} autoComplete="email" />
                </label>
                <label className="contents">
                  <span className="sr-only">{t("contact:form.message")}</span>
                  <textarea name="message" className={`${FIELD} min-h-[120px] resize-none`} placeholder={t("contact:form.message")} required minLength={10} maxLength={4000} />
                </label>
                {/* Tailwind's own reds: the design system defines `success` but no
                    error colour, and one message is not a reason to invent a token. */}
                {failed && (
                  <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[14px] text-red-700">
                    {t("contact:form.error")}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={sending}
                  className="mt-1 inline-flex items-center justify-center rounded-xl bg-primary px-6 py-3.5 font-display text-[15px] font-semibold text-white transition-colors hover:bg-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {sending ? t("contact:form.sending") : t("contact:form.submit")}
                </button>
              </form>
            )}
          </div>
        </Container>
      </section>
    </>
  );
}

